import type { MessageCatalog } from "./ko";

/**
 * 영어 문자열 사전.
 * `MessageCatalog` 타입으로 고정되어 있어 키가 누락되면 컴파일 오류가 발생한다.
 */
export const enMessages: MessageCatalog = {
  // 공통
  "common.confirm": "OK",
  "common.cancel": "Cancel",
  "common.cancelAction": "Cancel",
  "common.submitAction": "Submit",
  "common.save": "Save",
  "common.saving": "Saving",
  "common.savingEllipsis": "Saving...",
  "common.saveAction": "Save",
  "common.register": "Register",
  "common.registering": "Registering",
  "common.delete": "Delete",
  "common.deleting": "Deleting",
  "common.edit": "Edit",
  "common.editing": "Updating",
  "common.editInfo": "Edit info",
  "common.close": "Close",
  "common.next": "Next",
  "common.previous": "Back",
  "common.agree": "Agree",
  "common.send": "Send",
  "common.sending": "Sending",
  "common.select": "Select",
  "common.selectDate": "Select date",
  "common.all": "All",
  "common.processing": "Processing",
  "common.processingEllipsis": "Processing...",
  "common.checking": "Checking...",
  "common.loading": "Loading",
  "common.loadingSentence": "Loading...",
  "common.noData": "No data",
  "common.noDataToShow": "There is no data to display.",
  "common.noSearchResult": "No search results.",
  "common.activate": "Activate",
  "common.deactivate": "Deactivate",
  "common.refresh": "Reload",
  "common.backToLogin": "Back to login",
  "common.notProvidedShort": "-",
  "common.duration.seconds": "{seconds}s",
  "common.duration.minutes": "{minutes}m",
  "common.duration.minutesSeconds": "{minutes}m {seconds}s",
  "common.duration.hours": "{hours}h",
  "common.duration.hoursMinutes": "{hours}h {minutes}m",
  "common.duration.hoursMinutesSeconds": "{hours}h {minutes}m {seconds}s",
  "common.complianceAria": "Adherence {rate}%",
  "common.count.people": "{count}",
  "common.count.cases": "{count}",
  "common.round": "Session {round}",
  "common.score": "{score}",

  // 페이지네이션
  "pagination.first": "First page",
  "pagination.previous": "Previous page",
  "pagination.next": "Next page",
  "pagination.last": "Last page",

  // 내비게이션
  "nav.dashboard": "Dashboard",
  "nav.patientList": "Patients",
  "nav.doctorList": "Clinicians",
  "nav.hospitalList": "Hospitals",
  "nav.log": "Logs",
  "nav.patientNotification": "Send patient notification",
  "nav.goToDashboard": "Go to dashboard",
  "nav.goToHome": "Go to home",

  // 사용자 메뉴
  "account.privacy": "Privacy",
  "account.terms": "Terms of use",
  "account.changePassword": "Change password",
  "account.logout": "Log out",
  "account.menu": "User menu",

  // 언어 설정
  "language.title": "Language",
  "language.menuLabel": "Language",
  "language.selectLanguage": "Select language",
  "language.currentSuffix": "(current)",

  // 로그인
  "login.title": "Log in",
  "login.description": "Enter your email address (ID) to log in.",
  "login.descriptionApproved": "Only approved users can sign in.",
  "login.email": "Email",
  "login.emailPlaceholder": "Enter your email (ID)",
  "login.password": "Password",
  "login.passwordPlaceholder": "Enter your password",
  "login.rememberEmail": "Remember my email address (ID)",
  "login.submitting": "Logging in...",
  "login.forgotPassword": "Forgot your password?",
  "login.findPassword": "Find password",
  "login.mockAccount": "mock account",
  "login.mockAccountSelect": "Select mock account",
  "login.mockAccountPlaceholder": "Select an account",
  "login.error.invalidCredentials": "The ID or password does not match.",
  "login.error.invalidCredentialsWithDetail": "The ID or password does not match.\n{message}",
  "login.error.tooManyAttempts": "Login attempts are restricted. Please try again later.",
  "login.error.tooManyAttemptsWithDetail": "Login attempts are restricted.\n{message}",
  "login.error.deactivated": "This account is deactivated. Please contact an administrator.",
  "login.error.failed": "Login failed. Please try again.",
  "login.error.webRoleNotAllowed":
    "The SAT-014 web app is available to clinicians and administrators only.",
  "login.browser.title": "We recommend a different browser",
  "login.browser.description":
    "Some features may be limited in the current browser. We recommend using Chrome or Edge.",

  // 비밀번호 관련
  "findPassword.title": "Find password",
  "findPassword.description":
    "Enter your registered email address (ID) and we will send a password reset link by email.",
  "findPassword.emailLabel": "Email (ID)",
  "findPassword.emailPlaceholder": "Enter your email (ID)",
  "findPassword.submit": "Send reset link",
  "findPassword.submitting": "Sending",
  "findPassword.error.emailRequired": "Please enter your email address (ID).",
  "findPassword.error.emailFormat": "Please enter a valid email address.",
  "findPassword.error.requestFailed":
    "An error occurred while sending the password reset link.",
  "findPassword.complete.title": "Email sent",
  "findPassword.complete.sentTo": "We sent a password reset email to {email}.",
  "findPassword.complete.helper": "Use the link in the email to set a new password.",
  "findPassword.complete.spamHelper":
    "If you cannot find the email, please check your spam or promotions folder.",

  "resetPassword.title": "Reset password",
  "resetPassword.description": "Please enter a new password.",
  "resetPassword.newPassword": "New password",
  "resetPassword.newPasswordPlaceholder": "8-20 characters with letters, numbers and symbols",
  "resetPassword.confirmPassword": "Confirm new password",
  "resetPassword.confirmPasswordPlaceholder": "Re-enter your new password",
  "resetPassword.submit": "Change password",
  "resetPassword.submitting": "Changing",
  "resetPassword.completeTitle": "Password changed",
  "resetPassword.completeChanged": "Your password has been changed.",
  "resetPassword.completeRelogin": "Please log in again for security.",
  "resetPassword.error.linkInvalid": "The password reset link has expired or is invalid.",
  "resetPassword.error.tokenMissing": "The password reset token is missing.",
  "resetPassword.error.tokenMissingHelp":
    "The password reset token is missing. Please open the reset link in your email again.",
  "resetPassword.error.changeFailed": "An error occurred while changing the password.",

  "initPassword.title": "Set clinician password",
  "initPassword.description": "Set a new password to start using your account.",
  "initPassword.submit": "Set password",
  "initPassword.submitting": "Setting",
  "initPassword.completeTitle": "Password set",
  "initPassword.completeSet": "Your password has been set.",
  "initPassword.completeLogin": "Please log in for security.",
  "initPassword.error.linkInvalid": "The password setup link has expired or is invalid.",
  "initPassword.error.tokenMissing": "The password setup token is missing.",
  "initPassword.error.setFailed": "An error occurred while setting the password.",

  "changePassword.title": "Reset password",
  "changePassword.description": "Please set a new password for security.",
  "changePassword.currentPassword": "Current password",
  "changePassword.currentPasswordPlaceholder": "Enter your current password",
  "changePassword.newPassword": "New password",
  "changePassword.newPasswordPlaceholder": "Enter a new password",
  "changePassword.confirmPassword": "Confirm new password",
  "changePassword.confirmPasswordPlaceholder": "Re-enter the new password",
  "changePassword.submit": "Change password",
  "changePassword.submitting": "Changing",
  "changePassword.success": "Your password has been changed.",

  // 환자 앱 계정 설정
  "accountSetup.checkName.description":
    "Please verify your identity\nto set your SAT-014 ID and password.",
  "accountSetup.checkName.nameLabel": "Name",
  "accountSetup.checkName.namePlaceholder": "Enter your name",
  "accountSetup.checkName.error.nameRequired": "Please enter your name.",
  "accountSetup.checkName.error.nameMismatchTitle": "Name error",
  "accountSetup.checkName.error.nameMismatch": "The name you entered does not match.",
  "accountSetup.expiredTitle": "Verification expired",
  "accountSetup.expiredNotFound": "The verification information was not found or has expired.",
  "accountSetup.expiredResend": "Please request a new verification URL.",
  "accountSetup.requestErrorTitle": "Request error",
  "accountSetup.requestError": "An error occurred while processing the request.",
  "accountSetup.setup.description": "Set the ID and password you will use in the app.",
  "accountSetup.setup.userIdLabel": "ID",
  "accountSetup.setup.userIdPlaceholder": "6-16 lowercase letters and numbers",
  "accountSetup.setup.passwordLabel": "Password",
  "accountSetup.setup.passwordPlaceholder": "8-20 characters with letters, numbers and symbols",
  "accountSetup.setup.confirmPasswordLabel": "Confirm password",
  "accountSetup.setup.confirmPasswordPlaceholder": "Re-enter your password",
  "accountSetup.setup.complete": "Finish account setup",
  "accountSetup.setup.error.userIdRequired": "Please enter an ID.",
  "accountSetup.setup.error.userIdInvalid": "This ID cannot be used.",
  "accountSetup.setup.error.userIdTaken": "This ID is already in use.",
  "accountSetup.setup.error.passwordCondition": "Please check the password requirements.",
  "accountSetup.setup.error.passwordMismatch": "The passwords do not match.",
  "accountSetup.setup.error.setupFailed": "An error occurred during account setup.",
  "accountSetup.complete.done": "Account setup is complete.",
  "accountSetup.complete.loginGuide": "Log in to the SAT-014 app with your new account.",
  "accountSetup.expired.linkExpired": "The account setup link has expired.",
  "accountSetup.expired.beforeSetupPoint": "have not finished account setup",
  "accountSetup.expired.beforeSetupGuide":
    ", ask your registered hospital to resend the account setup message.",
  "accountSetup.expired.afterSetupPoint": "have completed account setup",
  "accountSetup.expired.afterSetupGuide": ", you can start using SAT-014.",
  "accountSetup.consent.terms": "Terms of service",
  "accountSetup.consent.privacy": "Privacy policy",

  "nameField.required": "Please enter the {label}.",
  "nameField.minLength": "The {label} must be at least 2 characters.",
  "nameField.noNumber": "The {label} cannot contain numbers.",
  "nameField.noSpecialCharacter": "The {label} cannot contain special characters.",
  "nameField.charset": "The {label} may contain only Korean, Latin letters and spaces.",

  // 비밀번호 / 아이디 규칙
  "password.rule.length": "Use 8-20 characters.",
  "password.rule.letter": "Include a letter.",
  "password.rule.number": "Include a number.",
  "password.rule.special": "Include one of the symbols !@#$%^&*.",
  "password.confirm.required": "Please re-enter the new password.",
  "password.confirm.mismatch": "The new passwords do not match.",
  "password.error.condition": "Please check the password requirements.",
  "appId.rule.length": "Use 6-16 characters.",
  "appId.rule.charset": "Only lowercase letters and numbers are allowed.",

  // 세션 / 공통 오류
  "session.duplicateLogin.title": "Duplicate login",
  "session.duplicateLogin.description":
    "Your session ended because the account signed in on another device or browser.",
  "session.expired.title": "Session expired",
  "session.expired.description": "Your login session has expired.",
  "session.reloginGuide": "Please log in again.",
  "error.requestFailed": "An error occurred while processing the request.",
  "error.requestEmpty": "The request information is missing.",
  "error.boundary.title": "This screen could not be loaded.",
  "error.boundary.message": "Please try again shortly or go to the dashboard.",
  "error.boundary.detail": "Error details",
  "notFound.title": "Page not found.",
  "notFound.description": "The requested path does not exist.",
  "toast.closeNotification": "Close notification",

  // 역할
  "role.admin": "Administrator",
  "role.medicalExpert": "Clinician",
  "role.nurse": "Nurse",
  "role.patient": "Patient",
  "role.guardian": "Guardian",
  "role.cro": "CRO",
  "role.unknown": "Unknown",

  // 성별
  "gender.male": "Male",
  "gender.female": "Female",
  "gender.unspecified": "Not specified",
  "gender.notSelected": "Prefer not to say",

  // 상태
  "status.inProgress": "In progress",
  "status.notStarted": "Not started",
  "status.completed": "Completed",
  "status.incomplete": "Incomplete",
  "status.canceled": "Canceled",
  "status.stopped": "Stopped",
  "status.noTreatment": "No treatment",
  "status.attentionNeeded": "Attention needed",
  "status.active": "Active",
  "status.inactive": "Inactive",
  "status.unknown": "Unknown",
  "status.treatmentInProgress": "Treatment in progress",
  "status.activityCompleted": "Activities complete (treatment ended)",
  "status.filter.viewAll": "View all",

  // 대시보드
  "dashboard.metric.totalPatients": "Total patients",
  "dashboard.metric.inProgressPatients": "Patients in progress",
  "dashboard.metric.lowCompliance": "Adherence below 60%",
  "dashboard.comparedToLastMonth": "vs. last month",
  "dashboard.patientList": "Patient list",
  "dashboard.viewAll": "View all",
  "dashboard.column.nameWithScreeningId": "Name (screening ID)",
  "dashboard.column.name": "Name",
  "dashboard.column.screeningId": "Screening ID",
  "dashboard.column.compliance": "Adherence",
  "dashboard.column.status": "Status",
  "dashboard.column.treatmentRound": "Treatment round",
  "dashboard.tablePagination": "Dashboard table pagination",
  "dashboard.treatmentRoundValue": "Round {count}",

  // 환자
  "patient.title": "Patient info",
  "patient.titleWithScreeningId": "Patient info ({screeningId})",
  "patient.treatmentInfoTitle": "Patient treatment info",
  "patient.noScreeningId": "This patient has no screening ID.",
  "patient.noName": "This patient has no name.",
  "patient.select": "Select a patient",
  "patient.selectDescription": "Select a patient from the list to see their details.",
  "patient.filter": "Patient filter",
  "patient.register": "Register patient",
  "patient.search": "Search patients",
  "patient.searchPlaceholder": "Search by name, phone number or email...",
  "patient.searchByNamePlaceholder": "Search by patient name...",
  "patient.searchByName": "Search by patient name",
  "patient.clearSearch": "Clear search",
  "patient.empty": "There are no patients.",
  "patient.loadingList": "Loading the patient list.",
  "patient.emptySearchData": "No patient data matches your search.",
  "patient.emptyDashboardData": "No patient data was loaded from the dashboard.",
  "patient.backToList": "Back to patient list",
  "patient.generalInfo": "General info",
  "patient.bodyInfo": "Physical info",
  "patient.restrictedInfo": "Restricted info",
  "patient.noEmail": "No email on file",
  "patient.noPhone": "No phone number on file",
  "patient.noBirthday": "No date of birth on file",
  "patient.emptyMemo": "The memo is empty.",
  "patient.birthdayWithAge": "{date} [age {age}]",
  "patient.screeningIdValue": "Screening ID {screeningId}",
  "patient.treatmentCount": "{count} treatment(s)",
  "patient.resendSetupSms": "Resend account setup SMS",
  "patient.addTreatment": "Add treatment",
  "patient.treatmentList": "Treatments",
  "patient.column.status": "Status",
  "patient.column.progress": "Progress",
  "patient.column.period": "Period",
  "patient.column.doctor": "Attending clinician",
  "patient.create.title": "Register a new patient",
  "patient.create.subtitle": "Enter the patient information to create a new account.",
  "patient.edit.title": "Edit patient info",
  "patient.edit.subtitle": "Enter the patient information to update the account.",
  "patient.section.personalInfo": "Personal info",
  "patient.section.personalInfoSection": "Personal info section",
  "patient.section.additionalInfo": "Additional info",
  "patient.section.bodyInfoSection": "Physical info section",
  "patient.section.etcInfoSection": "Other info section",
  "patient.field.name": "Name",
  "patient.field.phone": "Phone number",
  "patient.field.email": "Email",
  "patient.field.birthday": "Date of birth",
  "patient.field.gender": "Sex",
  "patient.field.memo": "Memo",
  "patient.field.memoPlaceholder": "Enter any notes or remarks",
  "patient.deactivate.title": "Deactivate this patient account?",
  "patient.deactivate.description":
    "If a treatment is in progress, end it first and then deactivate the patient account.",
  "patient.toast.feedbackSaved": "Feedback saved.",
  "patient.toast.accountCreated":
    "Account created. The account details will be sent to that email address.",
  "patient.toast.updated": "Patient information updated.",
  "patient.toast.setupSmsResent": "The account setup SMS has been resent.",
  "patient.toast.deactivated": "The patient account has been deactivated.",
  "patient.toast.activated": "The patient account has been activated.",
  "patient.toast.messageSent": "Message sent",
  "patient.toast.activityUuidMissing":
    "The activity UUID is missing, so the details cannot be loaded.",
  "patient.validation.title": "Please check the patient registration details.",
  "patient.validation.updateTitle": "Please check the patient details.",
  "patient.validation.nameRequired": "Please enter a name.",
  "patient.validation.nameMinLength": "The name must be at least 2 characters.",
  "patient.validation.emailFormat": "This is not a valid email address",
  "patient.validation.countryRequired": "Please select a country.",
  "patient.validation.countryUnsupported": "This country is not supported.",
  "patient.validation.countryCodeRequired": "Please enter a country code.",
  "patient.validation.timeZoneRequired": "Please select a region.",
  "patient.validation.genderRequired": "Please select a sex.",
  "patient.validation.dateRequired": "Please enter a date.",
  "patient.validation.dateLength": "The date must be 8 digits.",
  "patient.validation.dateFormat": "This date format is invalid (YYYYMMDD).",
  "patient.validation.dateFormatDash": "Enter the date of birth as YYYY-MM-DD.",
  "patient.validation.dateInvalid": "This date is not valid.",
  "patient.validation.birthdayFuture": "The date of birth cannot be in the future.",
  "patient.compliance": "Adherence: {rate}%",

  // 의료진
  "doctor.select": "Select a clinician",
  "doctor.selectDescription": "Select an account from the list to see its details.",
  "doctor.register": "Register clinician",
  "doctor.search": "Search by clinician name",
  "doctor.empty": "No clinicians are registered.",
  "doctor.emptyHelper": "Check your search term and filters, or register a new account.",
  "doctor.generalInfo": "General info",
  "doctor.patientList": "Patient list",
  "doctor.column.screeningId": "Screening ID",
  "doctor.column.treatmentStatus": "Treatment status",
  "doctor.column.accountStatus": "Account status",
  "doctor.noPatients": "No assigned patients.",
  "doctor.noPhone": "No phone number on file",
  "doctor.noMemo": "No memo on file",
  "doctor.resendPasswordSetup": "Resend password setup message",
  "doctor.toast.passwordSetupResent": "The password setup message has been resent.",
  "doctor.toast.created": "The clinician account has been created.",
  "doctor.toast.updated": "The clinician information has been updated.",
  "doctor.toast.activated": "The clinician account has been activated.",
  "doctor.toast.deactivated": "The clinician account has been deactivated.",
  "doctor.create.title": "Register clinician",
  "doctor.create.subtitle": "Enter the clinician information to create a new account.",
  "doctor.edit.title": "Edit clinician info",
  "doctor.edit.subtitle": "You can update the clinician account information.",
  "doctor.deactivate.title": "Deactivate this clinician account?",
  "doctor.deactivate.description":
    "A deactivated clinician account cannot be used until it is activated again.",
  "doctor.section.basicInfo": "Basic info",
  "doctor.section.affiliationInfo": "Affiliation and other info",
  "doctor.field.name": "Name",
  "doctor.field.namePlaceholder": "Enter the clinician's name",
  "doctor.field.email": "Email",
  "doctor.field.hospital": "Hospital",
  "doctor.field.phone": "Phone number",
  "doctor.field.memo": "Memo",
  "doctor.field.memoPlaceholder": "Enter any notes or remarks",
  "doctor.nameLabel": "Clinician name",
  "doctor.validation.title": "Please check the clinician registration details.",
  "doctor.validation.nameRequired": "Please enter the clinician's name.",
  "doctor.validation.emailRequired": "Please enter an email address.",
  "doctor.validation.emailFormat": "Please enter a valid email address.",
  "doctor.validation.hospitalRequired": "Please enter the hospital.",
  "doctor.usageWithLastLogin": "Active (last login: {date})",
  "doctor.beforeUsage": "Not yet used",

  // 병원
  "hospital.select": "Select a hospital",
  "hospital.selectDescription": "Select a hospital from the list to see its details.",
  "hospital.listTitle": "Hospitals",
  "hospital.countSummary": "{count} hospital(s)",
  "hospital.register": "Register hospital",
  "hospital.searchPlaceholder": "Search by hospital name or code",
  "hospital.doctorCount": "Clinicians {count}",
  "hospital.patientCount": "Patients {count}",
  "hospital.empty": "No hospitals are registered.",
  "hospital.emptyHelper": "Check your search term or register a hospital.",
  "hospital.info": "Hospital info",
  "hospital.field.code": "Hospital code",
  "hospital.registeredDoctorCount": "Registered clinicians",
  "hospital.registeredPatientCount": "Registered patients",
  "hospital.delete.title": "Delete hospital",
  "hospital.delete.subtitle": "Delete {name}?",
  "hospital.delete.description":
    "After deletion, the hospital can no longer be found in the hospital list.",
  "hospital.create.title": "Register hospital",
  "hospital.create.subtitle": "Enter the hospital information to register a new hospital.",
  "hospital.field.name": "Hospital name",
  "hospital.field.namePlaceholder": "Enter the full hospital name",
  "hospital.section.basicInfo": "Basic info",
  "hospital.toast.deleted": "The hospital has been deleted.",
  "hospital.toast.deleteBlocked":
    "Only hospitals with no registered clinicians or patients can be deleted.",
  "hospital.toast.created": "The hospital has been registered.",
  "hospital.validation.title": "Please check the hospital registration details.",
  "hospital.validation.nameRequired": "Please enter the full hospital name.",
  "hospital.validation.nameMinLength": "The hospital name must be at least 2 characters.",
  "hospital.validation.nameCharset":
    "The hospital name may contain only Korean, Latin letters and spaces.",
  "hospital.validation.codeRequired": "Please enter the hospital code.",
  "hospital.validation.codeFormat": "The hospital code must be 3 letters.",
  "hospital.loadingList": "Loading hospitals",
  "hospital.selectPrompt": "Select a hospital",
  "hospital.noneRegistered": "No hospitals are registered",
  "hospital.createNew": "Register a new hospital",
  "hospital.codeValue": "Hospital code {code}",

  // 치료
  "treatment.infoTitle": "Treatment info",
  "treatment.notFound": "Treatment information could not be found.",
  "treatment.sendEncouragement": "Send encouragement",
  "treatment.stop": "Stop treatment",
  "treatment.stopping": "Stopping",
  "treatment.stopConfirmSubtitle": "Stop the current treatment?",
  "treatment.stopConfirmDescription": "After stopping, the treatment status changes to ended.",
  "treatment.stopShort": "Stop",
  "treatment.compliance": "Adherence",
  "treatment.progressRate": "Progress",
  "treatment.performanceRate": "Completion",
  "treatment.summaryAria":
    "Adherence {compliance}, progress {progress}, completion {performance}",
  "treatment.field.number": "Treatment number",
  "treatment.field.currentStatus": "Current status",
  "treatment.field.period": "Period",
  "treatment.field.doctor": "Attending clinician",
  "treatment.activityLog": "Treatment activity log",
  "treatment.activityLogFilter": "Activity log filter",
  "treatment.activityLogPagination": "Treatment activity log pagination",
  "treatment.column.date": "Date",
  "treatment.column.status": "Status",
  "treatment.column.activityType": "Activity type",
  "treatment.column.startTime": "Start time",
  "treatment.column.endTime": "End time",
  "treatment.filter.regular": "Show regular activities",
  "treatment.filter.practice": "Show practice activities",
  "treatment.filter.all": "Show all activities",
  "treatment.weekly.empty": "There is no weekly performance data.",
  "treatment.weekly.noData": "No data",
  "treatment.weekly.week": "Week {week}",
  "treatment.weekly.compliance": "Adherence: {rate}%",
  "treatment.activityPerformanceAria": "{activityName} completion {rate}",
  "treatment.dayLabel": "{date} (day {day})",
  "treatment.progressSuffix": " · progress {rate}%",
  "treatment.summary": "Treatment summary",
  "treatment.nameWithType": "Treatment {type}",
  "treatment.name": "Treatment",
  "treatment.create.title": "Add treatment",
  "treatment.create.subtitle":
    "Add a new treatment for this patient. Enter the required details and press Save.",
  "treatment.create.startDate": "Start date",
  "treatment.create.endDate": "End date",
  "treatment.create.type": "Treatment type",
  "treatment.create.submitting": "Adding treatment...",
  "treatment.create.days": "{days} days",
  "treatment.toast.stopped": "The treatment has been stopped.",
  "treatment.toast.added": "The treatment has been added.",
  "treatment.toast.uuidMissing": "The treatment UUID is missing, so it cannot be stopped.",

  // 활동
  // 활동 이름/유형은 환자 앱(sat_014_app_rn: src/i18n/locales/<lang>/record.ts, common.ts)의 표기를 기준으로 한다.
  "activity.type.internal": "Internal Activity",
  "activity.type.external": "External Activity",
  "activity.type.practice": "Practice Activity",
  "activity.name.cloud": "Make the Sky Clear",
  "activity.name.ground": "Grounding Your Steps",
  "activity.name.energy": "Add Vitality",
  "activity.name.eye": "Eye Movement",
  "activity.name.walk": "Take a Walk",
  "activity.name.nature": "Enjoy Nature",
  "activity.name.breath": "Take a Deep Breath",
  "activity.name.deepBreath": "Deep Breathing",
  "activity.detail.title": "Heart rate chart and activity data",
  "activity.detail.notFound": "The activity details could not be found.",
  "activity.detail.summary": "Activity summary",
  "activity.detail.heartRateChart": "Heart rate chart",
  "activity.detail.heartRateDescription":
    "The heart rate chart shows heartbeats over time. The 120 bpm threshold is drawn as a red line, and ranges where the heart rate exceeded 120 bpm or where several readings were collected for the same activity time are shaded in grey.",
  "activity.detail.heartRateEmpty": "There is no heart rate data",
  "activity.detail.heartRateValueAria": "Heart rate {bpm} bpm",
  "activity.detail.perActivityEmpty": "There is no per-activity detail data",
  "activity.detail.groundData": "Grounding Your Steps data",
  "activity.detail.energyData": "Add Vitality data",
  "activity.detail.thoughtTitle": "Distressing & reframed thoughts",
  "activity.detail.thoughtAdded": "Added thoughts",
  "activity.detail.thoughtRevised": "Reframed thoughts",
  "activity.detail.performedActivity": "Completed activity",
  "activity.detail.rating": "Rating",
  "activity.detail.eyeData": "Eye Movement data",
  "activity.detail.eyeChart": "Eye Movement chart",
  "activity.detail.deepBreathPeriod": "Deep breathing {period}",
  "activity.detail.averageHeartRate": "Average heart rate",
  "activity.detail.minHeartRate": "Minimum heart rate",
  "activity.detail.maxHeartRate": "Maximum heart rate",
  "activity.detail.discomfortBefore": "Discomfort before activity",
  "activity.detail.discomfortAfter": "Discomfort after activity",
  "activity.detail.walkStep": "Steps",
  "activity.detail.feedback": "Activity feedback",
  "activity.detail.feedbackInput": "Feedback input",
  "activity.detail.feedbackPlaceholder": "Enter your feedback...",
  "activity.detail.feedbackAddPrompt": "Click here to add feedback.",
  "activity.detail.feedbackAdd": "Add feedback",
  "activity.eyeDirection.horizontal": "Horizontal",
  "activity.eyeDirection.pendulum": "Pendulum",
  "activity.eyeDirection.vertical": "Vertical",
  "activity.eyeDirection.default": "Eye Movement",

  // 알림 전송
  "notification.statusFilter": "Treatment status filter",
  "notification.selectAll": "Select all",
  "notification.deselectAll": "Clear selection",
  "notification.recipientList": "Recipients",
  "notification.noRecipient": "No recipients selected",
  "notification.addRecipient": "Add recipients",
  "notification.subject": "Subject",
  "notification.subjectPlaceholder": "Enter a subject...",
  "notification.body": "Message body",
  "notification.bodyPlaceholder": "Enter a message...",
  "notification.selectPatientTitle": "Select patients",
  "notification.sendTitle": "Send notification",
  "notification.sendConfirm": "Send the notification to the selected patients?",
  "notification.toast.sent": "Notification sent",
  "notification.encouragement.title": "Send encouragement",
  "notification.encouragement.subjectPlaceholder": "Subject",
  "notification.encouragement.bodyPlaceholder": "Message body",

  // 운영 로그
  "log.column.date": "Date",
  "log.column.userType": "User type",
  "log.column.logType": "Log type",
  "log.column.content": "Content",
  "log.empty": "There are no logs.",
  "log.pagination": "Operation log pagination",

  // 전화번호
  "phone.country.us": "United States",
  "phone.country.kr": "South Korea",
  "phone.validation.required": "Please enter a phone number.",
  "phone.validation.format": "This is not a valid phone number. (e.g. {example})",
};
